import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { UrlApi } from "../../../services/apirest";
import { formatoPesoArgentino } from "../../../services/library";
import DefinicionVariableEspecial from "../clientesEnvasado/fichaCliente/preciosEspeciales/ActualizarPrecios/General/DefinicionVariableEspecial";
import { DONDE, registrarAccion } from "../logs/registrarAccion";

function ActualizarPreciosMasivo({ open, onClose, seleccionados, onGuardado }) {
  const [monto, setMonto] = useState("");
  const [porcentaje, setPorcentaje] = useState("");
  const [precioMinimo, setPrecioMinimo] = useState("");
  const [tipoOperacion, setTipoOperacion] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const validarInput = (value) => /^-?\d*\.?\d*$/.test(value);

  const handleMontoChange = (e) => {
    const value = e.target.value;
    if (validarInput(value)) {
      setMonto(value);
      if (value !== "") setPorcentaje("");
      setTipoOperacion("monto");
    }
  };

  const handlePorcentajeChange = (e) => {
    const value = e.target.value;
    if (validarInput(value)) {
      setPorcentaje(value);
      if (value !== "") setMonto("");
      setTipoOperacion("porcentaje");
    }
  };

  const handlePrecioMinimo = (e) => {
    const value = e.target.value;
    if (validarInput(value)) {
      setPrecioMinimo(value);
      if (value !== "") setMonto("");
      setTipoOperacion("precio");
    }
  };

  // Calcula el nuevo precio de cada fila seleccionada (misma lógica que la ficha del cliente)
  const vistaPrevia = useMemo(() => {
    const valor = parseFloat(
      tipoOperacion === "monto"
        ? monto
        : tipoOperacion === "porcentaje"
        ? porcentaje
        : precioMinimo
    );
    if (isNaN(valor)) return [];

    return seleccionados.map((item) => {
      const anterior = Number(item.precio_minimo) || 0;
      let nuevo;
      if (tipoOperacion === "monto") {
        nuevo = anterior + valor;
      } else if (tipoOperacion === "porcentaje") {
        nuevo = anterior + anterior * (valor / 100);
      } else {
        nuevo = valor;
      }
      return { ...item, precio_nuevo: Math.round(nuevo) };
    });
  }, [seleccionados, tipoOperacion, monto, porcentaje, precioMinimo]);

  const hayNegativos = vistaPrevia.some((item) => item.precio_nuevo < 0);

  const isGuardarDisabled =
    guardando || vistaPrevia.length === 0 || hayNegativos;

  const handleConfirmar = async () => {
    setGuardando(true);
    setError("");
    try {
      // 1) Actualizo precios en lote
      const resp = await fetch(UrlApi + "/envasado_precios_especiales/multiple", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          vistaPrevia.map((item) => ({
            id: item.id,
            precio_minimo: item.precio_nuevo,
          }))
        ),
      });
      if (!resp.ok) throw new Error("Error al actualizar precios");

      // 2) Guardo historial
      const historial = vistaPrevia.map((item) => ({
        producto: item.producto,
        cliente: item.cliente,
        precio_minimo_anterior: item.precio_minimo,
        precio_minimo_nuevo: item.precio_nuevo,
        fecha: moment().format("YYYY-MM-DD"),
        usuario: localStorage.getItem("user"),
      }));
      const respHist = await fetch(UrlApi + "/envasado_historial_especial/multiple", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(historial),
      });
      if (!respHist.ok) console.error("Error guardando historial");

      const detalle =
        tipoOperacion === "monto"
          ? `monto ${monto}`
          : tipoOperacion === "porcentaje"
          ? `porcentaje ${porcentaje}%`
          : `precio fijo ${precioMinimo}`;
      registrarAccion(
        DONDE.PRECIOS,
        `Actualizó ${vistaPrevia.length} precios especiales (${detalle})`
      );

      onGuardado();
    } catch (err) {
      console.error("Error actualizando precios", err);
      setError("No se pudieron actualizar los precios.");
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} fullWidth maxWidth="lg">
      <Box bgcolor="#1F618D">
        <DialogTitle>
          <Typography fontSize={20} align="center" color="#ffffff">
            Actualizar {seleccionados.length} precios especiales
          </Typography>
        </DialogTitle>
      </Box>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <DefinicionVariableEspecial
              monto={monto}
              porcentaje={porcentaje}
              precioMinimo={precioMinimo}
              handleMontoChange={handleMontoChange}
              handlePorcentajeChange={handlePorcentajeChange}
              handlePrecioMinimo={handlePrecioMinimo}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography ml={1} mt={1} variant="subtitle1">
              Vista previa
            </Typography>
            <TableContainer sx={{ maxHeight: 400, border: "1px solid #ccc", borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell align="right">Nuevo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(vistaPrevia.length > 0 ? vistaPrevia : seleccionados).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.nombre_cliente}</TableCell>
                      <TableCell>{item.nombre_producto}</TableCell>
                      <TableCell align="right">
                        {formatoPesoArgentino(item.precio_minimo)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: "bold",
                          color: item.precio_nuevo < 0 ? "error.main" : "inherit",
                        }}
                      >
                        {item.precio_nuevo !== undefined
                          ? formatoPesoArgentino(item.precio_nuevo)
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {hayNegativos && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Hay precios que quedarían negativos.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", p: 1 }}>
        <Button
          color="error"
          variant="contained"
          onClick={onClose}
          disabled={guardando}
          sx={{ minWidth: 120 }}
        >
          Volver
        </Button>
        <Button
          color="success"
          variant="contained"
          onClick={handleConfirmar}
          disabled={isGuardarDisabled}
          sx={{ minWidth: 120 }}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ActualizarPreciosMasivo;
